"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

const UUID_VERSION_BYTE = 6;
const UUID_VARIANT_BYTE = 8;

/**
 * Generate a random v4 UUID. Prefers the native crypto.randomUUID and falls
 * back to building one from crypto.getRandomValues with the version (0100)
 * and variant (10) bits set per RFC 4122.
 */
function randomUuid(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // biome-ignore lint/suspicious/noBitwiseOperators: RFC 4122 requires setting the version bits
  bytes[UUID_VERSION_BYTE] = (bytes[UUID_VERSION_BYTE] & 0x0f) | 0x40;
  // biome-ignore lint/suspicious/noBitwiseOperators: RFC 4122 requires setting the variant bits
  bytes[UUID_VARIANT_BYTE] = (bytes[UUID_VARIANT_BYTE] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    ""
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function formatUuid(
  uuid: string,
  uppercase: boolean,
  noHyphens: boolean
): string {
  let out = uuid;
  if (noHyphens) {
    out = out.replaceAll("-", "");
  }
  if (uppercase) {
    out = out.toUpperCase();
  }
  return out;
}

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 100;

/** English strings. A translated content file overrides these through `ui`. */
export const UUID_GENERATOR_STRINGS = {
  howManyLabel: "How many? (1–100)",
  uppercase: "Uppercase",
  noHyphens: "No hyphens",
  quantityError: "Enter a quantity from 1 to 100.",
  generate: "Generate",
  copyAll: "Copy All",
  copied: "Copied!",
  uuid: "one:UUID|other:UUIDs",
  copy: "Copy",
} as const;

export default function UuidGeneratorWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(UUID_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [quantity, setQuantity] = useState("5");
  const [uppercase, setUppercase] = useState(false);
  const [noHyphens, setNoHyphens] = useState(false);
  const [uuids, setUuids] = useState<string[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const quantityNum = parseIntegerInput(quantity);
  const quantityValid =
    quantityNum !== null &&
    quantityNum >= MIN_QUANTITY &&
    quantityNum <= MAX_QUANTITY;

  const formatted = uuids.map((u) => formatUuid(u, uppercase, noHyphens));

  function handleGenerate() {
    if (quantityNum === null || !quantityValid) {
      return;
    }
    setUuids(Array.from({ length: quantityNum }, () => randomUuid()));
    setCopiedIndex(null);
    setCopiedAll(false);
  }

  function handleCopyOne(index: number) {
    writeToClipboard(formatted[index]).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setCopiedAll(false);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  function handleCopyAll() {
    if (formatted.length === 0) {
      return;
    }
    writeToClipboard(formatted.join("\n")).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedAll(true);
      setCopiedIndex(null);
      setTimeout(() => setCopiedAll(false), 2000);
    });
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="uuid-quantity"
            >
              {strings.howManyLabel}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="uuid-quantity"
              inputMode="numeric"
              max={MAX_QUANTITY}
              min={MIN_QUANTITY}
              onChange={(e) => setQuantity(e.target.value)}
              type="number"
              value={quantity}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={uppercase}
              className="accent-primary"
              onChange={(e) => setUppercase(e.target.checked)}
              type="checkbox"
            />
            {strings.uppercase}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={noHyphens}
              className="accent-primary"
              onChange={(e) => setNoHyphens(e.target.checked)}
              type="checkbox"
            />
            {strings.noHyphens}
          </label>
        </div>
        {!quantityValid && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
            {strings.quantityError}
          </p>
        )}
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!quantityValid}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generate}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={formatted.length === 0}
          onClick={handleCopyAll}
          type="button"
        >
          {copiedAll ? strings.copied : strings.copyAll}
        </button>
      </div>

      {/* Results */}
      {formatted.length > 0 && (
        <section className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {fmt(formatted.length)}{" "}
            {tPlural(strings.uuid, locale, formatted.length)}
          </p>
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
            {formatted.map((uuid, index) => (
              <li
                className="flex items-center justify-between gap-3 bg-card p-3"
                key={uuids[index]}
              >
                <code className="overflow-x-auto font-mono text-foreground text-sm">
                  {uuid}
                </code>
                <button
                  className="shrink-0 rounded-lg border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => handleCopyOne(index)}
                  type="button"
                >
                  {copiedIndex === index ? strings.copied : strings.copy}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
